/**
 * CR318 + CR322: Global Production Health Store
 *
 * Refactored: types, checks, and classification extracted to
 * src/hooks/production-health/ for maintainability.
 */

import React, { useState, useCallback, useEffect, createContext, useContext, type ReactNode } from "react";
import type {
  ProductionHealthState,
  ProductionHealthContextValue,
  HealthClassification,
  HealthStatus,
  HealthLabel,
} from "./production-health/types";
import {
  checkSnapshot,
  checkEdge,
  checkAuth,
  checkDb,
  runEdgeProbe,
  captureSupabaseConfig,
  buildEdgeDebugSummary,
} from "./production-health/checks";
import { classifyHealth } from "./production-health/classify";

// Re-export types for consumers
export type { HealthStatus, HealthLabel, HealthConfidence } from "./production-health/types";
export type {
  SnapshotCheck,
  EdgeCheck,
  AuthCheck,
  DbCheck,
  EdgeProbe,
  SupabaseConfigSnapshot,
  FetchErrorDetail,
  HealthClassification,
  ProductionHealthState,
  ProductionHealthActions,
  ProductionHealthContextValue,
} from "./production-health/types";

// ================================================================
// DEFAULT STATE
// ================================================================

const DEFAULT_CLASSIFICATION: HealthClassification = {
  status: "healthy",
  label: null,
  reason: "Health checks not yet run",
  secondaryReason: null,
  confidence: "low",
};

const DEFAULT_STATE: ProductionHealthState = {
  classification: DEFAULT_CLASSIFICATION,
  checks: { snapshot: null, edge: null, auth: null, db: null },
  edgeProbe: null,
  supabaseConfig: null,
  edgeDebugSummary: null,
  updatedAt: null,
  isRunning: false,
  drawerOpen: false,
};

// ================================================================
// CONTEXT
// ================================================================

const ProductionHealthContext = createContext<ProductionHealthContextValue | null>(null);

export function ProductionHealthProvider({ children }: { children: ReactNode }) {
  const [state, setState] = useState<ProductionHealthState>(DEFAULT_STATE);

  const CACHE_TTL_MS = 30000;

  const shouldRefresh = useCallback(() => {
    if (!state.updatedAt) return true;
    return Date.now() - new Date(state.updatedAt).getTime() > CACHE_TTL_MS;
  }, [state.updatedAt]);

  // ================================================================
  // TRIAGE
  // ================================================================

  const runTriage = useCallback(async () => {
    setState(prev => ({ ...prev, isRunning: true }));

    try {
      // Run all checks + probe + config in parallel
      const [snapshot, edge, auth, db, probe] = await Promise.all([
        checkSnapshot(),
        checkEdge(),
        checkAuth(),
        checkDb(),
        runEdgeProbe(),
      ]);

      const config = captureSupabaseConfig();
      const classification = classifyHealth(snapshot, edge, auth, db);
      const debugSummary = buildEdgeDebugSummary(edge, probe, config);

      setState(prev => ({
        ...prev,
        classification,
        checks: { snapshot, edge, auth, db },
        edgeProbe: probe,
        supabaseConfig: config,
        edgeDebugSummary: debugSummary,
        updatedAt: new Date().toISOString(),
        isRunning: false,
      }));
    } catch (err) {
      console.error("[ProductionHealth] Triage error:", err);
      setState(prev => ({
        ...prev,
        isRunning: false,
        classification: {
          status: "degraded",
          label: "Unknown",
          reason: "Health triage failed unexpectedly.",
          secondaryReason: null,
          confidence: "low",
        },
      }));
    }
  }, []);

  // ================================================================
  // DIAGNOSTICS
  // ================================================================

  const copyDiagnostics = useCallback(async (): Promise<boolean> => {
    try {
      const diagnostics = {
        checkedAt: state.updatedAt,
        classification: state.classification,
        checks: {
          snapshot: state.checks.snapshot,
          edge: state.checks.edge,
          auth: {
            status: state.checks.auth?.status,
            hasSession: state.checks.auth?.hasSession,
            hasToken: state.checks.auth?.hasToken,
          },
          db: state.checks.db,
        },
        edgeProbe: state.edgeProbe,
        supabaseConfig: state.supabaseConfig,
        edgeDebugSummary: state.edgeDebugSummary,
        mode: "live",
        userAgent: navigator.userAgent,
      };

      await navigator.clipboard.writeText(JSON.stringify(diagnostics, null, 2));
      return true;
    } catch (err) {
      console.error("[ProductionHealth] Copy diagnostics error:", err);
      return false;
    }
  }, [state]);

  // ================================================================
  // DRAWER CONTROLS
  // ================================================================

  const openDrawer = useCallback(() => {
    setState(prev => ({ ...prev, drawerOpen: true }));
  }, []);

  const closeDrawer = useCallback(() => {
    setState(prev => ({ ...prev, drawerOpen: false }));
  }, []);

  const toggleDrawer = useCallback(() => {
    setState(prev => ({ ...prev, drawerOpen: !prev.drawerOpen }));
  }, []);

  // ================================================================
  // AUTO-RUN ON MOUNT
  // ================================================================

  useEffect(() => {
    if (shouldRefresh()) {
      runTriage();
    }
  }, []);

  const value: ProductionHealthContextValue = {
    ...state,
    runTriage,
    copyDiagnostics,
    openDrawer,
    closeDrawer,
    toggleDrawer,
  };

  return (
    <ProductionHealthContext.Provider value={value}>
      {children}
    </ProductionHealthContext.Provider>
  );
}

export function useProductionHealth(): ProductionHealthContextValue {
  const context = useContext(ProductionHealthContext);
  if (!context) {
    throw new Error("useProductionHealth must be used within a ProductionHealthProvider");
  }
  return context;
}

export function useProductionHealthStatus(): {
  status: HealthStatus;
  label: HealthLabel;
  isHealthy: boolean;
} {
  const context = useContext(ProductionHealthContext);
  if (!context) {
    return { status: "healthy", label: null, isHealthy: true };
  }
  return {
    status: context.classification.status,
    label: context.classification.label,
    isHealthy: context.classification.status === "healthy",
  };
}
